/**
 * CalcTooltip Component
 * Shows a "how is this calculated?" info icon that expands on click
 * to reveal the calculation methodology and source data.
 * Used across the dashboard for calculation transparency.
 */

import { useState, useCallback, useRef, useEffect } from 'react';

interface CalcTooltipProps {
  title: string;
  children: React.ReactNode;
}

export function CalcTooltip({ title, children }: CalcTooltipProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const toggle = useCallback(() => setOpen((prev) => !prev), []);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <span className="calc-tooltip" ref={ref}>
      <button
        className="calc-tooltip__trigger"
        onClick={toggle}
        aria-expanded={open}
        aria-label={`${title} — calculation details`}
        title="How is this calculated?"
        type="button"
      >
        &#9432;
      </button>
      {open && (
        <div className="calc-tooltip__popup" role="tooltip">
          <div className="calc-tooltip__header">
            <span className="calc-tooltip__title">{title}</span>
            <button
              className="calc-tooltip__close"
              onClick={() => setOpen(false)}
              aria-label="Close"
              type="button"
            >
              &times;
            </button>
          </div>
          <div className="calc-tooltip__body">
            {children}
          </div>
        </div>
      )}
    </span>
  );
}
